import Vue from 'vue';
import axios from 'axios';
import CampaignModal from '@/components/common/CampaignModal.vue';
import { shouldOpenCampaign } from './campaignPopup';
import store from '@/store';
import router from '@/router';

let inFlight = null; // 多重起動ガード
const KEY_PREFIX = 'reech.campaign.lastSeen.v2.';
const TZ = 'Asia/Tokyo';
const root = process.env.VUE_APP_API;
const loadListAPI = root + '/api/list/load_list';

// JSTのYYYYMMDD（時刻無視）
const ymd = (d = new Date()) =>
    new Intl.DateTimeFormat('ja-JP', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
        .format(d).replace(/\D/g, '');

// 'YYYYMMDD' → Date(JST 0時) / 不正は null
const parseYmd = s => /^\d{8}$/.test(s)
    ? new Date(`${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6)}T00:00:00+09:00`)
    : null;

// 月加算（暦）
const plusMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };

/**
 * SNS問わず、クライアントが最後に更新したリスト詳細へ遷移する。
 * 対象リストがない場合はリスト一覧へフォールバックする。
 *
 * @returns {Promise<void>}
 */
async function navigateToLatestUpdatedList() {
    const token = store.state.token;
    const userToken = store.state.user_token;

    try {
        const response = await axios.get(loadListAPI, {
            params: {
                token,
                user_token: userToken,
                offset: 0,
                sort_key: 'update_at',
                sort_order: 'desc',
                person_id: 'all',
                target_client_id: 'all',
                select_tag_id: 'all',
                sns_type: 'all',
                type: 'min',
            },
        });

        const latestList = response.data && response.data.mylist && response.data.mylist[0];
        if (latestList && latestList.list_id) {
            await router.push({
                name: 'listDetail',
                params: { list_id: latestList.list_id },
            });
            return;
        }
    } catch (_) { /* no-op: 一覧へフォールバック */ }

    await router.push({ name: 'list' });
}

export async function runCampaignGate(slug) {
    const key = KEY_PREFIX + slug;

    // 再表示判定：不正値は削除、6か月未到来なら終了、到来済みなら旧値削除
    try {
        const raw = localStorage.getItem(key);
        if (raw) {
            const last = parseYmd(raw);
            if (!last) {
                localStorage.removeItem(key);
            } else {
                if (parseYmd(ymd()) < plusMonths(last, 6)) return;
                localStorage.removeItem(key);
            }
        }
    } catch (_) { /* no-op */ }

    if (inFlight) return inFlight; // すでに出している最中なら同じPromiseを返す

    const { open, cfg } = await shouldOpenCampaign(slug);
    if (!open) return;

    // 画像プリロード（ロード失敗ならモーダルを出さない）
    try {
        await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('image load failed'));
            img.src = cfg.image;
        });
    } catch (e) {
        // eslint-disable-next-line no-console
        // console.warn('[campaign] image preload failed:', cfg.image);
        return; // 画像がない場合はモーダルを出さずに終了
    }

    inFlight = new Promise(resolve => {
        const ModalCtor = Vue.extend(CampaignModal);
        const el = document.createElement('div');
        document.body.appendChild(el);

        const vm = new ModalCtor({
            store,
            router,
            propsData: { image: cfg.image, visible: true }
        });

        const markSeenAndCleanup = () => {
            try { localStorage.setItem(key, ymd()); } catch (_) { /* no-op */ }
            try {
                vm.$destroy();
            } catch (_) { /* no-op */ }
            if (vm.$el && vm.$el.parentNode) {
                vm.$el.parentNode.removeChild(vm.$el);
            }
            inFlight = null;
            resolve();
        };

        vm.$on('close', () => {
            markSeenAndCleanup();
        });

        vm.$on('click-banner', async () => {
            try {
                await navigateToLatestUpdatedList();
            } finally {
                markSeenAndCleanup();
            }
        });

        vm.$mount(el);
    });

    return inFlight;
}
